import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { NotificationChannel } from '@tabita/contracts';
import { formatDateTime } from '@tabita/i18n';
import { useI18n } from '@/i18n/i18n-provider';
import { notificationsApi } from '@/services/notifications.api';
import { ApiError } from '@/services/api-client';
import { Alert, EmptyState, Spinner } from '@/components/ui';

const CHANNELS: readonly NotificationChannel[] = ['EMAIL', 'SMS', 'PUSH'];

/** Notifications: a read-only delivery log, most recent first. No detail page — a row has nothing else to show. */
export function NotificationsListPage() {
  const { t, locale } = useI18n();
  const [channel, setChannel] = useState<NotificationChannel | ''>('');

  const query = useQuery({
    queryKey: ['notifications', { channel }],
    queryFn: ({ signal }) =>
      notificationsApi.list({ channel: channel || undefined, limit: 50 }, signal),
    placeholderData: (previous) => previous,
  });

  const logs = query.data?.data ?? [];
  const isStale = query.isPlaceholderData;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('notifications.log.title')}</h1>
          {query.data && (
            <p className="page-count">
              {t('notifications.log.count', { count: logs.length })}
              {query.data.pagination.hasMore && '+'}
            </p>
          )}
        </div>
      </header>

      <div className="filters">
        <select
          className="field-input"
          value={channel}
          onChange={(event) => setChannel(event.target.value as NotificationChannel | '')}
        >
          <option value="">{t('notifications.log.channel')}</option>
          {CHANNELS.map((value) => (
            <option key={value} value={value}>
              {t(`notifications.channel.${value}`)}
            </option>
          ))}
        </select>
      </div>

      {query.isError && (
        <Alert tone="danger">
          {query.error instanceof ApiError
            ? t(`errors.${query.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && logs.length === 0 && <EmptyState title={t('notifications.log.empty')} />}

      {logs.length > 0 && (
        <div className="table-scroll" data-stale={isStale || undefined}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('notifications.log.channel')}</th>
                <th scope="col">{t('notifications.log.recipient')}</th>
                <th scope="col">{t('notifications.log.subject')}</th>
                <th scope="col">{t('notifications.log.provider')}</th>
                <th scope="col">{t('notifications.log.status')}</th>
                <th scope="col">{t('notifications.log.sentAt')}</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td>{t(`notifications.channel.${log.channel}`)}</td>
                  <td dir="ltr">{log.recipientMasked}</td>
                  <td>{log.subject ?? <span className="cell-empty">—</span>}</td>
                  <td>{log.provider}</td>
                  <td>
                    {log.accepted ? t('notifications.log.accepted') : t('notifications.log.failed')}
                  </td>
                  <td dir="ltr">{formatDateTime(log.sentAt, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
